 
import React, { useMemo, useState, useEffect, useContext } from 'react';
import { Row, Col } from 'react-bootstrap';
import moment from 'moment';
import { FaCheck, FaClock, FaArrowRight } from 'react-icons/fa';
import { ExternalLink } from 'lucide-react';
import { useTravel } from 'v3/pages/Travel';
import useModal from 'hooks/useModal';
import { UNDER_GRO_ANALYSIS_SLUG } from 'v3/utils/constants';
import { pluralize, formatTime, formatDateCustom, formatAddress } from 'v3/utils/formatter';
import theme from 'v3/theme';
import { validatePermission } from 'actions/index';
import Card, { DefaultLoadingBodyComponent } from 'v3/components/Card';
import Text from 'v3/components/Text';
import Button from 'v3/components/Button';
import { BlockedTravelContext } from 'contexts/BlockedTravelProvider';
import InformModal from './Inform';
import EffectOrigin from './EffectOrigin';
import * as Styled from './styles';

moment.locale('pt-br');

const actions = {
  delivery: 'Entrega',
  customhouse: 'Aduaneiro',
  collect: 'Coleta',
};

function getDisplayAddress(location) {
  if (!location) return '-';

  const formattedAddress = String(
    location?.formatted_address || location?.formattedAddress || '',
  ).trim();

  if (formattedAddress) {
    return formattedAddress;
  }

  return formatAddress(location);
}

function LocationItem({
  id,
  isOrigin,
  isCompleted,
  action,
  pointLabel,
  address,
  scheduledTime,
  arrivalTime,
  effectiveTime,
  startSchedule,
  hasStartSchedule,
  showWaitingColumn,
  client,
  rawAddress,
  onUpdateAddress,
}) {
  const { isTravelBlocked } = useContext(BlockedTravelContext);
  const [hasPermissionToEdit, setPermissionToEdit] = useState();
  const [informType, setInformType] = useState();
  const [waitingTime, setWaitingTime] = useState();
  const travel = useTravel();
  const informModal = useModal();
  const addressModal = useModal();
  const effectOriginModal = useModal();

  useEffect(() => {
    setPermissionToEdit(validatePermission('ALTERAR_VIAGEM'));
  }, []);

  useEffect(() => {
    if (!arrivalTime || !effectiveTime) {
      setWaitingTime(null);
      return;
    }

    const diff = moment(effectiveTime).diff(moment(arrivalTime), 'minutes');
    if (diff < 0) {
      setWaitingTime(null);
      return;
    }
    const hours = parseInt(diff / 60, 10);
    const minutes = diff % 60;

    if (typeof diff === 'number') {
      if (hours < 10) {
        if (minutes < 10) setWaitingTime(`0${hours}:0${minutes}`);
        else setWaitingTime(`0${hours}:${minutes}`);
      } else if (minutes < 10) setWaitingTime(`${hours}:0${minutes}`);
      else setWaitingTime(`${hours}:${minutes}`);
    } else {
      setWaitingTime(null);
    }
  }, [arrivalTime, effectiveTime]);

  if (travel.isLoading) {
    return null;
  }

  const hasArrival = Boolean(arrivalTime);
  const hasEffective = Boolean(effectiveTime);
  const isCompletedStatus = isCompleted || (hasArrival && hasEffective);
  const isInProgress = (hasArrival || hasEffective) && !isCompletedStatus;
  const statusBg = isCompletedStatus
    ? Styled.STATUS_COLORS.successBg
    : isInProgress
    ? Styled.STATUS_COLORS.warningBg
    : null;

  return (
    <>
      <InformModal
        id={id}
        isOpen={informModal.isOpen}
        onClose={informModal.close}
        type={informType}
        isOrigin={isOrigin}
      />
      <EffectOrigin isOpen={effectOriginModal.isOpen} onClose={effectOriginModal.close} id={id} />
      <Col xs={12}>
        <div
          className="route-line-wrapper"
          style={{
            borderRadius: 6,
            padding: 12,
            backgroundColor: statusBg || 'transparent',
          }}
        >
          <Row className="no-gutters align-items-stretch">
            <Col
              md={hasStartSchedule ? 3 : 5}
              xs={12}
              style={{ paddingRight: 12 }}
            >
              <div className="d-flex align-items-start">
                <div
                  style={{
                    position: 'relative',
                    width: 16,
                    minWidth: 16,
                    alignSelf: 'stretch',
                  }}
                >
                  <Styled.Marker.Tray
                    $completed={isCompletedStatus}
                    $progress={isInProgress}
                  />
                  <Styled.Marker
                    $completed={isCompletedStatus}
                    $progress={isInProgress}
                  >
                    {isCompletedStatus && <FaCheck color="#fff" size={9} />}
                  </Styled.Marker>
                </div>
                <div style={{ paddingLeft: 16 }}>
                  <Text as="div" color="dark" weight={600}>
                    {pointLabel ? `${action} #${pointLabel}` : action}
                  </Text>
                  <Text as="div" type="regular" color="gray">
                    {address}
                  </Text>
                  {client ? (
                    <Text as="div" type="regular" color="gray" className="my-2">
                      {`Cliente: ${client?.socialName} - ${client?.cpfCnpj}`}
                    </Text>
                  ) : null}
                </div>
              </div>
            </Col>
            <Col md={hasStartSchedule ? 9 : 7} xs={12} className="route-schedules-col">
              <Styled.Schedules
                $completed={isCompletedStatus}
                $progress={isInProgress}
                $noBg
                className="route-schedules-box"
              >
                <Styled.RouteColumnsGrid $hasStartSchedule={hasStartSchedule}>
                  <Styled.RouteGridBodyCell>
                    <div className="text-center w-100">
                      <Text as="div" color="dark" className="d-block d-sm-none mb-3">
                        Agendado
                      </Text>
                      <div className="d-flex flex-row justify-content-center">
                        {!!startSchedule && (
                          <>
                            <div>
                              <Text as="div" type="regular" color="dark">
                                {formatDateCustom(startSchedule, 'DD, MMMM [de] YYYY')}
                              </Text>
                              <span
                                className="d-inline-flex align-items-center"
                                style={{ gap: 4 }}
                              >
                                <FaClock color={theme.colors.dark} />
                                <Text type="label" color="dark">
                                  {formatTime(startSchedule)}
                                </Text>
                              </span>
                            </div>
                            <div className="mt-3 mx-2">
                              <FaArrowRight color={theme.colors.dark} />
                            </div>
                          </>
                        )}
                        <div>
                          <Text as="div" type="regular" color="dark">
                            {formatDateCustom(scheduledTime, 'DD, MMMM [de] YYYY')}
                          </Text>
                          <span
                            className="d-inline-flex align-items-center"
                            style={{ gap: 4 }}
                          >
                            <FaClock color={theme.colors.dark} />
                            <Text type="label" color="dark">
                              {formatTime(scheduledTime)}
                            </Text>
                          </span>
                        </div>
                      </div>
                    </div>
                  </Styled.RouteGridBodyCell>
                  <Styled.RouteGridBodyCell $showDivider>
                    <div className="text-center w-100">
                      <Text as="div" color="dark" className="d-block d-sm-none mb-3">
                        Chegada
                      </Text>
                      {arrivalTime ? (
                        <>
                          <Text as="div" type="regular" color="dark">
                            {formatDateCustom(arrivalTime, 'DD, MMMM [de] YYYY')}
                          </Text>
                          <span
                            className="d-inline-flex align-items-center"
                            style={{ gap: 4 }}
                          >
                            <FaClock color={theme.colors.dark} />
                            <Text type="label" color="dark">
                              {formatTime(arrivalTime)}
                            </Text>
                          </span>
                          {validatePermission('ALTERAR_HORARIO_ROTAS') && (
                            <div className="d-flex justify-content-center">
                              <Styled.EditButton
                                className="text-center"
                                variant="secondary"
                                size="sm"
                                disabled={isTravelBlocked || travel?.isDisabledGROAnalysis}
                                onClick={() => {
                                  informModal.open();
                                  setInformType('arrival_time');
                                }}
                              >
                                <Text type="regular" weight="500">
                                  Editar
                                </Text>
                              </Styled.EditButton>
                            </div>
                          )}
                        </>
                      ) : (
                        hasPermissionToEdit && (
                          <>
                            <Text as="div" color="dark" className="d-block d-sm-none mb-3">
                              Chegada
                            </Text>
                            <Button
                              disabled={
                                !travel.data?.driver ||
                                !travel.data?.vehicle?.[0]?.vehicles ||
                                isTravelBlocked ||
                                travel?.isDisabledGROAnalysis
                              }
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                informModal.open();
                                setInformType('arrival_time');
                              }}
                              title={
                                travel?.isDisabledGROAnalysis &&
                                'Não é possível edição durante período de análise GRO'
                              }
                            >
                              <Text type="regular" weight="500">
                                Informar
                              </Text>
                            </Button>
                          </>
                        )
                      )}
                    </div>
                  </Styled.RouteGridBodyCell>
                  <Styled.RouteGridBodyCell>
                    <div className="text-center w-100">
                      {showWaitingColumn && arrivalTime && effectiveTime && waitingTime && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.25rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <FaArrowRight color={theme.colors.dark} />
                          <FaClock color={theme.colors.dark} />
                          <Text type="label" color="dark">
                            {waitingTime}
                          </Text>
                          <FaArrowRight color={theme.colors.dark} />
                        </span>
                      )}
                    </div>
                  </Styled.RouteGridBodyCell>
                  <Styled.RouteGridBodyCell>
                    <div className="text-center w-100">
                      {effectiveTime ? (
                        <>
                          <Text as="div" color="dark" className="d-block d-sm-none mb-3">
                            Efetivado
                          </Text>
                          <Text as="div" type="regular" color="dark">
                            {formatDateCustom(effectiveTime, 'DD, MMMM [de] YYYY')}
                          </Text>
                          <span
                            className="d-inline-flex align-items-center"
                            style={{ gap: 4 }}
                          >
                            <FaClock color={theme.colors.dark} />
                            <Text type="label" color="dark">
                              {formatTime(effectiveTime)}
                            </Text>
                          </span>
                          {validatePermission('ALTERAR_HORARIO_ROTAS') && (
                            <div className="d-flex justify-content-center">
                              <Styled.EditButton
                                className="text-center"
                                variant="secondary"
                                size="sm"
                                disabled={isTravelBlocked || travel?.isDisabledGROAnalysis}
                                onClick={() => {
                                  informModal.open();
                                  setInformType('effective_time');
                                }}
                                title={
                                  travel?.isDisabledGROAnalysis &&
                                  'Não é possível edição durante período de análise GRO'
                                }
                              >
                                <Text type="regular" weight="500">
                                  Editar
                                </Text>
                              </Styled.EditButton>
                            </div>
                          )}
                        </>
                      ) : (
                        hasPermissionToEdit && (
                          <>
                            <Text as="div" color="dark" className="d-block d-sm-none mb-3">
                              Efetivado
                            </Text>
                            <Button
                              disabled={
                                !travel.data?.driver ||
                                !travel.data?.vehicle?.[0] ||
                                isTravelBlocked ||
                                travel?.isDisabledGROAnalysis
                              }
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                if (!isOrigin) {
                                  informModal.open();
                                  setInformType('effective_time');
                                } else {
                                  effectOriginModal.open();
                                }
                              }}
                              title={
                                travel?.isDisabledGROAnalysis &&
                                'Não é possível edição durante período de análise GRO'
                              }
                            >
                              <Text type="regular" weight="500">
                                Informar
                              </Text>
                            </Button>
                          </>
                        )
                      )}
                    </div>
                  </Styled.RouteGridBodyCell>
                </Styled.RouteColumnsGrid>
              </Styled.Schedules>
            </Col>
          </Row>
        </div>
      </Col>
    </>
  );
}

export default function Locations({ travelDetailsRef, onUpdateAddress }) {
  const travel = useTravel();
  let origin = useMemo(
    () => travel.data?.origin || travel.data?.loads?.loadOrigins?.[0],
    [travel.data?.origin, travel.data?.loads?.loadOrigins],
  );

  const [waitingHours, setWaitingHours] = useState(null);
  const [waitingMinutes, setWaitingMinutes] = useState(null);
  const [hasPermissionToEdit, setPermissionToEdit] = useState(false);

  useEffect(() => {
    setPermissionToEdit(validatePermission('ALTERAR_VIAGEM'));
  }, []);

  const canEditRoute = useMemo(() => {
    const statusId = travel.data?.statusId;
    // os IDs para permitir:
    const allowedStatuses = [2, 8, 11, 13];
    const allPoints = [
      ...(travel.data?.loads?.loadOrigins || []),
      ...(travel.data?.loads?.loadDestinations || []),
    ];
    const hasAnyEditablePoint = allPoints.some((p) => !p.arrivalTime && !p.effectiveTime);

    const isBlockedByRiskAnalysis = travel.data?.riskApproved === UNDER_GRO_ANALYSIS_SLUG;

    return (
      hasPermissionToEdit &&
      !!travel.data?.driver &&
      !!travel.data?.vehicle?.length &&
      !travel.isDisabledGROAnalysis &&
      !isBlockedByRiskAnalysis &&
      allowedStatuses.includes(statusId) &&
      hasAnyEditablePoint
    );
  }, [
    hasPermissionToEdit,
    travel.data?.statusId,
    travel.data?.driver,
    travel.data?.vehicle,
    travel?.isDisabledGROAnalysis,
    travel.data?.riskApproved,
    travel.data?.loads?.loadOrigins,
    travel.data?.loads?.loadDestinations,
  ]);

  const destinations = useMemo(
    () => travel.data?.destinations || travel.data?.loads?.loadDestinations,
    [travel.data?.destinations, travel.data?.loads?.loadDestinations],
  );

  const isOriginCompleted = useMemo(
    () => Boolean(origin?.arrivalTime && origin?.effectiveTime),
    [travel.data.statusId, origin],
  );

  useEffect(() => {
    const points = [origin, ...(destinations || [])];
    let hasWaitingPoints = false;

    const totalMinutes = points.reduce((acc, point) => {
      if (!point?.arrivalTime || !point?.effectiveTime) {
        return acc;
      }

      const diff = moment(point.effectiveTime).diff(moment(point.arrivalTime), 'minutes');
      if (!Number.isFinite(diff) || diff < 0) {
        return acc;
      }

      hasWaitingPoints = true;
      return acc + diff;
    }, 0);

    if (!hasWaitingPoints) {
      setWaitingHours(null);
      setWaitingMinutes(null);
      return;
    }

    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
    const minutes = String(totalMinutes % 60).padStart(2, '0');

    setWaitingHours(hours);
    setWaitingMinutes(minutes);
  }, [origin, destinations]);

  const hasAnyWaitingColumn = useMemo(() => {
    const points = [origin, ...(destinations || [])];

    return points.some((point) => {
      if (!point?.arrivalTime || !point?.effectiveTime) {
        return false;
      }

      const diff = moment(point.effectiveTime).diff(moment(point.arrivalTime), 'minutes');
      return Number.isFinite(diff) && diff >= 0;
    });
  }, [origin, destinations]);

  if (!travel.data.id) {
    return null;
  }
  const hasStartSchedule = !!(
    travel.data?.loads?.loadOrigins?.[0]?.startSchedule ||
    travel.data?.loads?.loadDestinations?.map((item) => item.startSchedule)[0] !== null
  );

  return (
    <Styled.GlobalRouteLine>
      <Card
        ref={travelDetailsRef}
        loading={travel.isLoading}
        LoadingBodyComponent={() => <DefaultLoadingBodyComponent linesCount={4} perLine={2} />}
        header={
          <div
            className="d-flex justify-content-between align-items-center w-100"
            style={{ paddingRight: '1rem' }}
          >
            <div>
              <Text type="header" color="dark">
                Rota da viagem
              </Text>
            </div>

            {canEditRoute && (
              <div className="ml-auto">
                <Button
                  variant="primary"
                  onClick={() => window.open(`/cargas/${travel.data?.loadId}#route`, '_blank')}
                >
                  Editar rota da carga
                  <ExternalLink size={16} style={{ marginLeft: 8 }} />
                </Button>
              </div>
            )}
          </div>
        }
      >
        <div className="route-line-header">
          <Row className="no-gutters align-items-stretch">
            <Col md={hasStartSchedule ? 3 : 5} xs={12} style={{ paddingRight: 12 }}>
              <div className="mb-3">
                <Text type="body" color="dark" weight={600}>
                  Origem
                </Text>
              </div>
            </Col>
            <Col md={hasStartSchedule ? 9 : 7} className="d-none d-sm-block">
              <Styled.RouteColumnsGrid $hasStartSchedule={hasStartSchedule}>
                <Styled.RouteGridCell>
                  <Text type="body" color="dark" weight={600}>
                    Agendado
                  </Text>
                </Styled.RouteGridCell>
                <Styled.RouteGridCell>
                  <Text type="body" color="dark" weight={600}>
                    Chegada
                  </Text>
                </Styled.RouteGridCell>
                <Styled.RouteGridCell />
                <Styled.RouteGridCell>
                  <Text type="body" color="dark" weight={600}>
                    Efetivado
                  </Text>
                </Styled.RouteGridCell>
              </Styled.RouteColumnsGrid>
            </Col>
          </Row>
        </div>
      <Styled.SectionDivider />
      <Row className="mb-3 d-flex align-items-stretch no-gutters">
        <LocationItem
          id={origin?.id}
          isOrigin
          action="Coleta"
          pointLabel="A"
          isCompleted={isOriginCompleted}
          address={getDisplayAddress(origin)}
          rawAddress={origin}
          scheduledTime={origin?.scheduledTime}
          arrivalTime={origin?.arrivalTime}
          startSchedule={origin?.startSchedule}
          hasStartSchedule={hasStartSchedule}
          effectiveTime={origin?.effectiveTime}
          client={origin?.client}
          showWaitingColumn={hasAnyWaitingColumn}
          onUpdateAddress={onUpdateAddress}
        />
      </Row>
      <Styled.SectionTitle>
        <Text type="body" color="dark" weight={600}>
          {pluralize('Destino', destinations.length)}
        </Text>
      </Styled.SectionTitle>
      {destinations.map((destination, index) => {
        /**
         * @param {number} num
         * @returns {string}
         */
        function numberToLetters(num) {
          let letters = '';
          while (num >= 0) {
            letters = String.fromCharCode((num % 26) + 65) + letters;
            num = Math.floor(num / 26) - 1;
          }
          return letters;
        }
        const isCompleted = Boolean(
          destination.arrivalTime && destination.effectiveTime,
        );

        const action = actions[destination.action.toLowerCase()] || 'Coleta';
        const pointLabel = numberToLetters(index + 1); 

        return (
          <Row key={destination.id} className="no-gutters">
            <LocationItem
              key={destination.id}
              id={destination.id}
              action={action}
              pointLabel={pointLabel}
              isCompleted={isCompleted}
              address={getDisplayAddress(destination)}
              rawAddress={destination}
              scheduledTime={destination.scheduledTime}
              arrivalTime={destination.arrivalTime}
              effectiveTime={destination.effectiveTime}
              startSchedule={destination?.startSchedule}
              hasStartSchedule={hasStartSchedule}
              client={destination?.client}
              showWaitingColumn={hasAnyWaitingColumn}
              onUpdateAddress={onUpdateAddress}
            />
          </Row>
        );
      })}
      {waitingHours !== null && waitingMinutes !== null && (
        <Col className="text-center">
          <Text type="regular" color="gray">
            Tempo de espera total -
          </Text>
          <Text weight={500} type="regular">{` ${waitingHours}:${waitingMinutes}hrs`}</Text>
        </Col>
      )}
      </Card>
    </Styled.GlobalRouteLine>
  );
}
